/**
 * What the Decision Room keeps on the platform: the dates and estimates the
 * executive entered, and the decisions taken — each a site note, dated and
 * attributed, append-only for the log.
 */
import type { DataSource } from "../../api/dataSource";
import type { SourceRef } from "../../domain/decision";
import type { RoomDecision, RoomEntered } from "../../domain/decisionRoom";
import type { BoardSettings, Matter, Placement } from "../../domain/ceoRoom";
import { latestNote, putNote, readLocal, writeLocal } from "../shared/noteStore";

export const ROOM_TOPICS = {
  entered: "decision.room",
  log: "decision.room.log",
  matters: "decision.room.matters",
  board: "decision.room.board",
} as const;

export interface RoomRecord {
  id: string;
  at: string;
  by: string;
  decisionId: string;
  title: string;
  option: string;
  optionName: string;
  rationale: string;
  boardLine: string[];
  evidence: SourceRef[];
}

export async function loadRoomEntered(source: DataSource, siteId: string): Promise<RoomEntered> {
  const r = await latestNote<RoomEntered>(source, siteId, ROOM_TOPICS.entered);
  return r ? { ...r.value, enteredBy: r.value.enteredBy ?? r.by, enteredAt: r.value.enteredAt ?? r.at } : {};
}

export const saveRoomEntered = (source: DataSource, siteId: string, e: RoomEntered) =>
  putNote(source, siteId, ROOM_TOPICS.entered, e);

export async function loadRoomLog(source: DataSource, siteId: string): Promise<RoomRecord[]> {
  try {
    const notes = await source.listNotes(siteId, ROOM_TOPICS.log);
    const out: RoomRecord[] = [];
    for (const n of notes) {
      try {
        out.push(JSON.parse(n.body) as RoomRecord);
      } catch {
        /* not a record */
      }
    }
    if (out.length) return out;
  } catch {
    /* no platform */
  }
  return readLocal<RoomRecord[]>(siteId, ROOM_TOPICS.log) ?? [];
}

export async function appendRoomLog(
  source: DataSource,
  siteId: string,
  rec: RoomRecord,
): Promise<"platform" | "browser"> {
  const local = readLocal<RoomRecord[]>(siteId, ROOM_TOPICS.log) ?? [];
  writeLocal(siteId, ROOM_TOPICS.log, [rec, ...local]);
  try {
    await source.addNote(siteId, { topic: ROOM_TOPICS.log, category: "recommendation", body: JSON.stringify(rec) });
    return "platform";
  } catch {
    return "browser";
  }
}

export function recordFor(d: RoomDecision, option: string, by: string, rationale: string): RoomRecord {
  const o = d.options.find((x) => x.key === option);
  return {
    id: `${d.id}:${Date.now().toString(36)}`,
    at: new Date().toISOString(),
    by,
    decisionId: d.id,
    title: d.title,
    option,
    optionName: o?.name ?? option,
    rationale,
    boardLine: d.boardLine,
    evidence: d.evidence.map((e) => ({ kind: e.kind, ref: e.ref, label: e.label, href: e.href })),
  };
}

/* ── the executive's own matters, and the strategy board ─────────────── */

/** The matters, as one list: the latest note is the current set. */
export async function loadMatters(source: DataSource, siteId: string): Promise<Matter[]> {
  const r = await latestNote<Matter[]>(source, siteId, ROOM_TOPICS.matters);
  return Array.isArray(r?.value) ? r.value : [];
}

export const saveMatters = (source: DataSource, siteId: string, matters: Matter[]) =>
  putNote(source, siteId, ROOM_TOPICS.matters, matters);

export interface SavedBoard {
  placement: Placement;
  settings: BoardSettings;
  by?: string;
  at?: string;
}

export async function loadBoard(source: DataSource, siteId: string): Promise<SavedBoard | null> {
  const r = await latestNote<SavedBoard>(source, siteId, ROOM_TOPICS.board);
  return r?.value?.placement ? { ...r.value, by: r.value.by ?? r.by, at: r.value.at ?? r.at } : null;
}

export const saveBoard = (source: DataSource, siteId: string, board: SavedBoard) =>
  putNote(source, siteId, ROOM_TOPICS.board, board);

/** A decision on one of the executive's own matters, in the same log as the desk's. */
export function recordForMatter(
  m: Matter,
  option: string,
  by: string,
  rationale: string,
  summary: string[],
): RoomRecord {
  const o = m.options.find((x) => x.key === option);
  return {
    id: `${m.id}:${Date.now().toString(36)}`,
    at: new Date().toISOString(),
    by,
    decisionId: m.id,
    title: m.title,
    option,
    optionName: o?.name ?? option,
    rationale,
    boardLine: summary,
    evidence: [],
  };
}
